import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANG_CODES, type SupportedLangCode } from '@/application/i18n/language'
import { Label } from '@/ui/components/common/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/common/Select'

const LANGUAGE_META: Record<SupportedLangCode, { flag: string; nameKey: string }> = {
  fr: { flag: '🇫🇷', nameKey: 'translations.locale_fr' },
  en: { flag: '🇬🇧', nameKey: 'translations.locale_en' },
  es: { flag: '🇪🇸', nameKey: 'translations.locale_es' },
}

interface SourceLanguageSelectorProps {
  value: SupportedLangCode
  onChange: (value: SupportedLangCode) => void
  /** Locked once any translation exists (TRANSLATION-PLAN.md §5 decision 1)
   * — renders as a static label with an explanatory line instead of a
   * dropdown. */
  disabled?: boolean
}

/**
 * "Written in (fixed once translations exist)" — the source-language
 * selector shared by the create forms and by edit forms while the entity
 * has zero translation rows (TRANSLATION-PLAN.md §5). Default is French,
 * per plan and N1 (the whole existing corpus is French).
 */
export function SourceLanguageSelector({ value, onChange, disabled }: SourceLanguageSelectorProps) {
  const { t } = useTranslation()

  if (disabled) {
    return (
      <div className="space-y-1.5">
        <Label>{t('translations.source_language_label')}</Label>
        <p className="text-sm">
          {LANGUAGE_META[value].flag} {t(LANGUAGE_META[value].nameKey)}
        </p>
        <p className="text-sm text-muted-foreground">{t('translations.source_language_frozen')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="source-language-selector">{t('translations.source_language_label')}</Label>
      <Select
        value={value}
        onValueChange={(next) => {
          const match = SUPPORTED_LANG_CODES.find((code) => code === next)
          if (match) onChange(match)
        }}
      >
        <SelectTrigger id="source-language-selector" className="w-full sm:w-64">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANG_CODES.map((code) => (
            <SelectItem key={code} value={code}>
              {LANGUAGE_META[code].flag} {t(LANGUAGE_META[code].nameKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
